<template>
  <component :is="tourLayout">
    <div v-if="listing?.items" class="w-full">
      <AppBreadcrumb :trail="trail" />

      <header
        class="w-full max-w-7xl mx-auto px-3 xl:px-0 my-4 flex flex-col gap-2"
      >
        <h1 class="text-3xl font-black">{{ heading }}</h1>
        <p class="max-w-4xl text-gray-300">
          {{ intro }} See also the
          <NuxtLink to="/" class="underline">{{ latestLabel }}</NuxtLink>
          and
          <NuxtLink to="/tags" class="underline">{{ tagsLabel }}</NuxtLink>
          <span>.</span>
        </p>
      </header>

      <SectionContainer id="top-rated-videos">
        <template #header>
          <h2 class="text-2xl">Ranked by member score</h2>
        </template>
        <div class="card-grid">
          <ReleaseCard
            v-for="(release, i) in listing.items"
            :key="release.id"
            :release="release"
            :rank="rankOf(i)"
            :show-site-rank="true"
            :show-rating="true"
            :vertical="useVerticalCards"
          />
        </div>
      </SectionContainer>

      <Pagination
        v-if="totalPages > 1"
        resource="top-rated"
        :current-page="page"
        :total-pages="totalPages"
      />
    </div>
  </component>
</template>

<script setup>
  // The site's Top Rated list, ranked as on the homepage's Top Rated shelf,
  // 24 scenes a page. Each page is its own canonical, so pages 2+ get
  // indexed; ?page=1 redirects to the bare path so page 1 has one address.
  defineOptions({
    name: "TopRatedPage",
  })

  useRedirectInAgeRestrictedArea()

  const currentSite = inject("currentSite")
  const inAgeRestrictedLocation = inject("inAgeRestrictedLocation")
  const route = useRoute()
  const { notFoundUrl, useVerticalCards } = useSiteSettings(currentSite.value)
  const tourLayout = useTourLayout(currentSite.value)
  const path = "/top-rated"
  const page = Math.max(1, parseInt(route.query.page) || 1)
  const perPage = 24

  if (route.query.page === "1") {
    await navigateTo(path, { redirectCode: 301 })
  }

  const { data: listing } = await useApi("/api/releases", {
    query: {
      sort: "top_rated",
      page,
      per_page: perPage,
      age_restricted: inAgeRestrictedLocation,
      totals: true,
    },
  })

  const totalPages = computed(() => listing.value?.pagination?.totalPages || 1)

  if (!listing.value?.items) {
    await navigateTo(notFoundUrl, { external: true, redirectCode: 302 })
  } else if (page > totalPages.value) {
    await navigateTo(path, { redirectCode: 302 })
  }

  // The place on the whole list, not the scene's rank_site: pornplus's tour
  // list skips the ranks of scenes it doesn't show.
  const rankOf = (i) => (page - 1) * perPage + i + 1

  // The host useSiteSeo picks the page's copy by. Its name, not seoSiteName,
  // is the brand: the H1s spell it "Tiny4K", where seoSiteName says "Tiny 4K".
  const seoSite = computed(() => currentSite.value.alias || currentSite.value)
  const brand = computed(() => seoSite.value.name || currentSite.value.name)
  const latestLabel = computed(() => `latest ${brand.value} videos`)
  const tagsLabel = computed(() => `${brand.value} categories`)

  const heading = computed(
    () =>
      sitePageH1(seoSite.value.host, path) || `Top Rated ${brand.value} Videos`,
  )

  // "These are the 1,187 Tiny4K scenes with the highest member ratings, from
  // 101,476 ratings." The ratings clause is left out when the API has no count.
  const intro = computed(() => {
    const count = listing.value?.pagination?.totalItems ?? 0
    const votes = listing.value?.voteCount
    const ratings = votes
      ? `, from ${votes.toLocaleString("en-US")} ratings`
      : ""
    return (
      `These are the ${count.toLocaleString("en-US")} ${brand.value} scenes with the highest member ratings${ratings}. ` +
      "Rankings update as new votes come in."
    )
  })

  const trail = computed(() => [
    { name: currentSite.value.name, path: "/" },
    { name: "Top Rated" },
  ])

  if (listing.value?.items) {
    useSiteSeo(
      {
        defaultTitle: "Top Rated {{ siteName }} Videos",
        defaultDescription:
          "The highest-rated scenes on {{ siteName }}, ranked by member score. Browse the best {{ siteName }} videos, updated as new ratings come in.",
      },
      { image: () => listing.value?.items?.[0]?.thumbUrl },
      { canonicalPage: page },
    )

    const origin = publicSiteOrigin(useRequestURL().href)
    const pageUrl = (n) => `${origin}${n > 1 ? `${path}?page=${n}` : path}`

    const schema = computed(() =>
      buildCollectionPageSchema({
        origin,
        url: pageUrl(page),
        name: heading.value,
        description: intro.value,
        trail: trail.value,
        items: listing.value.items.map((release) => ({
          path: `/video/${release.cachedSlug}`,
          name: release.title,
        })),
        startPosition: rankOf(0),
      }),
    )

    useHead({
      link: computed(() =>
        [
          page > 1 && { rel: "prev", href: pageUrl(page - 1) },
          page < totalPages.value && { rel: "next", href: pageUrl(page + 1) },
        ].filter(Boolean),
      ),
      script: computed(() =>
        schema.value
          ? [
              {
                key: "schema-CollectionPage",
                type: "application/ld+json",
                innerHTML: serializeJsonLd(schema.value),
              },
            ]
          : [],
      ),
    })
  }
</script>
